import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import {
  MixedSchema,
  getSchemaSize,
  initVersionedSchema,
} from '@principle-theorem/editor';
import { PrincipleEditorModule } from '@principle-theorem/ng-interactions';
import { MenuButtonLoaderFn } from '@principle-theorem/ng-prosemirror';
import {
  EditMediaDialogComponent,
  IEditMediaDialogData,
  NgMediaManagerModule,
} from '@principle-theorem/ng-media-manager';
import {
  EmptyStateComponent,
  PdfService,
} from '@principle-theorem/ng-principle-shared';
import {
  DialogPresets,
  IMediaManagerUpdateEvent,
  NgCommonModule,
  TypedFormControl,
  UserIconComponent,
} from '@principle-theorem/ng-shared';
import {
  Interaction,
  ReferralMessage,
  ReferralNote,
} from '@principle-theorem/principle-core';
import {
  CommunicationDirection,
  ReferralActor,
  ReferralCommunicationType,
  ICommunicationGroup,
  ICommunicationItem,
  INCOMING_USER_ICON_COLOR,
  IReferral,
  IReferralClinicalNote,
  IReferralInteraction,
  IReferralMedia,
  IReferralMessage,
  IReferralNote,
  IStaffer,
  MEDIA_GROUPING_TIME_THRESHOLD_SECONDS,
  isReferralInteraction,
  isReferralMedia,
  ReferralCollection,
  CommunicationItemUnion,
} from '@principle-theorem/principle-core/interfaces';
import {
  CASUAL_DATE_WITH_YEAR,
  INamedDocument,
  TIME_FORMAT_UPPERCASE,
  WithRef,
  isSameIdentifier,
  shareReplayCold,
  toISODate,
} from '@principle-theorem/shared';
import { groupBy, orderBy, first, isEmpty } from 'lodash';
import { map, startWith } from 'rxjs/operators';
import { ReferralMessageService } from '../../services/referral-message.service';
import { ReferralNoteService } from '../../services/referral-note.service';
import { ReferralMediaMenuComponent } from '../referral-media-menu/referral-media-menu.component';
import { ReferralClinicalNoteDisplayComponent } from '../referral-clinical-note-display/referral-clinical-note-display.component';
import { ReferralCommunicationTypeFilterComponent } from '../referral-communication-type-filter/referral-communication-type-filter.component';
import { ReferralMediaService } from '../../services/referral-media.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

type CommunicationAddMode = 'message' | 'note' | 'pdf';

interface IAddModeOption {
  value: CommunicationAddMode;
  label: string;
  icon: string;
  tooltip: string;
}

@Component({
  selector: 'pr-referral-communication',
  imports: [
    NgCommonModule,
    PrincipleEditorModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatDividerModule,
    MatMenuModule,
    EmptyStateComponent,
    UserIconComponent,
    NgMediaManagerModule,
    ReferralMediaMenuComponent,
    ReferralClinicalNoteDisplayComponent,
    ReferralCommunicationTypeFilterComponent,
    MatProgressSpinnerModule,
    MatSelectModule,
  ],
  templateUrl: './referral-communication.component.html',
  styleUrl: './referral-communication.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ReferralMessageService, ReferralNoteService],
  host: { class: 'flex flex-col h-full overflow-x-hidden' },
})
export class ReferralCommunicationComponent implements AfterViewInit {
  private _destroyRef = inject(DestroyRef);
  private _referralMessageService = inject(ReferralMessageService);
  private _referralNoteService = inject(ReferralNoteService);
  private _dialog = inject(MatDialog);
  referralMediaService = inject(ReferralMediaService);
  pdfService = inject(PdfService);

  referral = input.required<WithRef<IReferral>>();
  messages = input<WithRef<IReferralMessage>[]>();
  interactions = input<WithRef<IReferralInteraction>[]>([]);
  media = input<WithRef<IReferralMedia>[]>([]);
  clinicalNotes = input<WithRef<IReferralClinicalNote>[]>([]);
  notes = input<WithRef<IReferralNote>[]>([]);
  currentUser = input.required<ReferralActor>();
  menuItems = input<MenuButtonLoaderFn[]>([]);
  canManagePatientMedia = input<boolean>(false);
  canAddNotes = input<boolean>(false);
  showInteractions = input<boolean>(false);

  patient = computed(() => this.referral().patient);
  isDisabled = signal(true);
  selectedCommunicationTypes = signal<ReferralCommunicationType[]>([]);
  communicationItemsByDate = computed<ICommunicationGroup[]>(() =>
    this._groupCommunicationItemsByDate()
  );
  editingNoteRef = signal<string | undefined>(undefined);
  selectedAddMode = signal<CommunicationAddMode>('message');
  addModeOptions = computed<IAddModeOption[]>(() => {
    const options: IAddModeOption[] = [
      {
        value: 'message',
        label: 'Message',
        icon: 'chat',
        tooltip: 'Add a message visible to everyone on the referral',
      },
    ];
    if (this.canAddNotes()) {
      options.push({
        value: 'note',
        label: 'Note',
        icon: 'text_snippet',
        tooltip: 'Add an internal note only visible to your team',
      });
    }
    options.push({
      value: 'pdf',
      label: 'Attach as PDF',
      icon: 'picture_as_pdf',
      tooltip: 'Convert your message to a PDF attachment',
    });
    return options;
  });

  readonly dateFormat = CASUAL_DATE_WITH_YEAR;
  readonly timeFormat = TIME_FORMAT_UPPERCASE;
  readonly communicationItemType = ReferralCommunicationType;
  readonly communicationDirection = CommunicationDirection;
  readonly incomingUserIconBackgroundColour = INCOMING_USER_ICON_COLOR;

  messageCtrl = new TypedFormControl<MixedSchema>(
    initVersionedSchema(),
    Validators.required
  );

  editNoteCtrl = new TypedFormControl<MixedSchema>(initVersionedSchema());

  @ViewChild('messagesContainer') messagesContainer!: ElementRef<HTMLElement>;

  constructor() {
    this._setupMessageScrolling();
    this._setupFormValidation();
  }

  ngAfterViewInit(): void {
    this._scrollToBottom();
  }

  async addMessage(): Promise<void> {
    if (this.isDisabled() || !this._isValidMessageInput()) {
      return;
    }

    const message = this._createMessage();
    this._resetMessageForm();
    this._scrollToBottom();

    await this._referralMessageService.upsert(this.referral(), message);
  }

  async addNote(): Promise<void> {
    if (this.isDisabled() || !this._isValidMessageInput()) {
      return;
    }

    const note = this._createNote();
    this._resetMessageForm();
    this._scrollToBottom();

    await this._referralNoteService.upsert(this.referral(), note);
  }

  async convertToPdf(): Promise<void> {
    if (this.isDisabled() || !this._isValidMessageInput()) {
      return;
    }

    const patientName = this.patient().name;
    const fileName = `${patientName}-referral.pdf`;
    const content = this.messageCtrl.value;

    await this.referralMediaService.uploadPdf(
      { body: content },
      fileName,
      this.referral(),
      this.currentUser()
    );

    this._resetMessageForm();
    this._scrollToBottom();
  }

  async executeAddAction(): Promise<void> {
    switch (this.selectedAddMode()) {
      case 'message':
        return this.addMessage();
      case 'note':
        return this.addNote();
      case 'pdf':
        return this.convertToPdf();
      default:
        return;
    }
  }

  asMessage(item: ICommunicationItem): WithRef<IReferralMessage> {
    return item.item as WithRef<IReferralMessage>;
  }

  asInteraction(item: ICommunicationItem): WithRef<IReferralInteraction> {
    return item.item as WithRef<IReferralInteraction>;
  }

  asMedia(item: ICommunicationItem): WithRef<IReferralMedia> {
    return item.item as WithRef<IReferralMedia>;
  }

  asClinicalNote(item: ICommunicationItem): WithRef<IReferralClinicalNote> {
    return item.item as WithRef<IReferralClinicalNote>;
  }

  asNote(item: ICommunicationItem): WithRef<IReferralNote> {
    return item.item as WithRef<IReferralNote>;
  }

  getInteractionIcon(
    item: WithRef<IReferralMessage> | WithRef<IReferralInteraction>
  ): string | undefined {
    return isReferralInteraction(item)
      ? Interaction.getInteractionTypeIcon(item.type) ?? undefined
      : undefined;
  }

  getInteractionLabel(
    item: WithRef<IReferralMessage> | WithRef<IReferralInteraction>
  ): string | undefined {
    return isReferralInteraction(item)
      ? Interaction.getInteractionTypeLabel(item.type) ?? undefined
      : undefined;
  }

  async viewMedia(selected: ICommunicationItem): Promise<void> {
    if (selected.type !== ReferralCommunicationType.Media) {
      return;
    }

    const media = first(selected.mediaGroup) ?? this.asMedia(selected);
    const allMedia = this.media();

    if (!allMedia) {
      return;
    }

    await this._openMediaDialog(media, allMedia);
  }

  onCommunicationTypeFilterChange(types: ReferralCommunicationType[]): void {
    this.selectedCommunicationTypes.set(types);
  }

  startEditNote(note: WithRef<IReferralNote>): void {
    this.editingNoteRef.set(note.ref.path);
    this.editNoteCtrl.setValue(note.content);
  }

  cancelEditNote(): void {
    this.editingNoteRef.set(undefined);
    this.editNoteCtrl.reset(initVersionedSchema());
  }

  async saveEditNote(note: WithRef<IReferralNote>): Promise<void> {
    const updatedNote: WithRef<IReferralNote> = {
      ...note,
      content: this.editNoteCtrl.value,
    };
    await this._referralNoteService.upsert(this.referral(), updatedNote);
    this.cancelEditNote();
  }

  async deleteNote(note: WithRef<IReferralNote>): Promise<void> {
    await this._referralNoteService.delete(note);
  }

  isEditingNote(note: WithRef<IReferralNote>): boolean {
    return this.editingNoteRef() === note.ref.path;
  }

  private _setupMessageScrolling(): void {
    effect(() => {
      const messages = this.messages();
      if (messages?.length) {
        setTimeout(() => this._scrollToBottom(), 0);
      }
    });
  }

  private _setupFormValidation(): void {
    this.messageCtrl.statusChanges
      .pipe(
        startWith(undefined),
        map(() => this._isFormDisabled()),
        shareReplayCold(),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((isDisabled) => this.isDisabled.set(isDisabled));
  }

  private _isFormDisabled(): boolean {
    return (
      !this.messageCtrl.valid ||
      !this.messageCtrl.dirty ||
      !getSchemaSize(this.messageCtrl.value)
    );
  }

  private _isValidMessageInput(): boolean {
    const content = this.messageCtrl.value;
    const actor = this.currentUser();
    return !!(content && actor);
  }

  private _createMessage(): IReferralMessage {
    return ReferralMessage.init({
      content: this.messageCtrl.value,
      actor: this.currentUser(),
    });
  }

  private _createNote(): IReferralNote {
    return ReferralNote.init({
      content: this.messageCtrl.value,
      actor: this.currentUser() as INamedDocument<IStaffer>,
    });
  }

  private _resetMessageForm(): void {
    this.messageCtrl.reset(initVersionedSchema(), { emitEvent: false });
    this.messageCtrl.updateValueAndValidity();
  }

  private async _openMediaDialog(
    media: WithRef<IReferralMedia>,
    allMedia: WithRef<IReferralMedia>[]
  ): Promise<void> {
    const config = DialogPresets.fullscreen<IEditMediaDialogData>({
      data: {
        orderedMedia: allMedia,
        editMode: false,
        readOnly: true,
        media,
      },
    });

    await this._dialog
      .open<
        EditMediaDialogComponent,
        IEditMediaDialogData,
        IMediaManagerUpdateEvent
      >(EditMediaDialogComponent, config)
      .afterClosed()
      .toPromise();
  }

  private _groupCommunicationItemsByDate(): ICommunicationGroup[] {
    const allItems = this._getAllCommunicationItems();
    const filteredItems = this._applyTypeFilter(allItems);
    const sortedItems = orderBy(filteredItems, 'createdAt.seconds', 'asc');
    const groupedByDate = groupBy(sortedItems, (item) =>
      toISODate(item.createdAt)
    );

    const groups = Object.entries(groupedByDate).map(([date, items]) => ({
      date,
      items: this._consolidateMediaGroups(items),
    }));

    return orderBy(groups, 'date', 'asc');
  }

  private _applyTypeFilter(
    items: CommunicationItemUnion[]
  ): CommunicationItemUnion[] {
    const selectedTypes = this.selectedCommunicationTypes();

    if (!selectedTypes.length) {
      return items;
    }

    return items.filter((item) => {
      const itemType = this._getItemType(item);
      return selectedTypes.includes(itemType);
    });
  }

  private _getAllCommunicationItems(): CommunicationItemUnion[] {
    const messages = this.messages() ?? [];
    const interactions = this.interactions();
    const media = this.media();
    const clinicalNotes = this.clinicalNotes();
    const notes = this.notes();

    return [...messages, ...interactions, ...media, ...clinicalNotes, ...notes];
  }

  private _consolidateMediaGroups(
    sortedItems: CommunicationItemUnion[]
  ): ICommunicationItem[] {
    const result: ICommunicationItem[] = [];
    let skipUntilIndex = -1;

    sortedItems.forEach((currentItem, index) => {
      if (index <= skipUntilIndex) {
        return;
      }

      if (!isReferralMedia(currentItem)) {
        result.push(this._toCommunicationItem(currentItem));
        return;
      }

      const mediaGroup = this._collectMediaGroup(sortedItems, index);
      result.push(this._createMediaGroupItem(mediaGroup));

      skipUntilIndex = index + mediaGroup.length - 1;
    });

    return result;
  }

  private _collectMediaGroup(
    items: CommunicationItemUnion[],
    startIndex: number
  ): WithRef<IReferralMedia>[] {
    const firstItem = items[startIndex] as WithRef<IReferralMedia>;
    const remainingItems = items.slice(startIndex + 1);
    const additionalMedia: WithRef<IReferralMedia>[] = [];

    for (const item of remainingItems) {
      if (!isReferralMedia(item)) {
        break;
      }

      const lastInGroup = isEmpty(additionalMedia)
        ? firstItem
        : additionalMedia[additionalMedia.length - 1];

      if (!this._shouldGroupMedia(item, lastInGroup)) {
        break;
      }

      additionalMedia.push(item);
    }

    return [firstItem, ...additionalMedia];
  }

  private _shouldGroupMedia(
    current: WithRef<IReferralMedia>,
    previous: WithRef<IReferralMedia>
  ): boolean {
    const timeDiff = Math.abs(
      current.createdAt.seconds - previous.createdAt.seconds
    );

    return (
      isSameIdentifier(current.actor, previous.actor) &&
      timeDiff <= MEDIA_GROUPING_TIME_THRESHOLD_SECONDS
    );
  }

  private _createMediaGroupItem(
    mediaGroup: WithRef<IReferralMedia>[]
  ): ICommunicationItem {
    const firstItem = mediaGroup[0];

    return {
      type: ReferralCommunicationType.Media,
      item: firstItem,
      mediaGroup,
      direction: this._getDirection(firstItem),
    };
  }

  private _toCommunicationItem(
    item: CommunicationItemUnion
  ): ICommunicationItem {
    return {
      type: this._getItemType(item),
      item,
      direction: this._getDirection(item),
    };
  }

  private _getItemType(
    item: CommunicationItemUnion
  ): ReferralCommunicationType {
    const collectionId = item.ref.parent.id;
    switch (collectionId) {
      case ReferralCollection.Media:
        return ReferralCommunicationType.Media;
      case ReferralCollection.Interactions:
        return ReferralCommunicationType.Interaction;
      case ReferralCollection.ClinicalNotes:
        return ReferralCommunicationType.ClinicalNote;
      case ReferralCollection.Notes:
        return ReferralCommunicationType.Note;
      case ReferralCollection.Messages:
      default:
        return ReferralCommunicationType.Message;
    }
  }

  private _getDirection(item: {
    actor: ReferralActor | INamedDocument<IStaffer>;
  }): CommunicationDirection {
    return isSameIdentifier(item.actor, this.currentUser())
      ? CommunicationDirection.Outbound
      : CommunicationDirection.Inbound;
  }

  private _scrollToBottom(): void {
    const container = this.messagesContainer?.nativeElement;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }
}
